import { Card, Group, Skeleton, Stack, useMatches } from "@mantine/core";
import { useEffect, useState } from "react";

import { MarketCarsTable } from "../MarketCarsTable";
import { MarketPerformanceChart } from "../MarketPerformanceChart";
import { PerformingAssets } from "../PerformingAssets";

import { Carousel } from "@/components/elements";

import type { MarketCar, MarketCarColumns } from "@/types/market";
import type { Performance } from "@/types/performance";
import type { FC } from "react";
import type { MarketCarsTableProps } from "../MarketCarsTable";

type CarsTableProps = Pick<
  MarketCarsTableProps,
  | "decade"
  | "hideComparison"
  | "makeId"
  | "namespace"
  | "onModelsCompare"
  | "pending"
  | "onPageChange"
  | "onPageSizeChange"
  | "onSearch"
  | "page"
  | "pageSize"
  | "totalCars"
>;

export interface MarketCarsDashboardProps {
  cars?: MarketCar[];
  carsProps?: CarsTableProps;
  columns?: MarketCarColumns[];
  getPath?: (carId: string) => string;
  loading?: boolean;
  makeOrDecade: string;
  performance?: Performance;
  performanceProps?: { loading?: boolean };
  topCars?: MarketCar[];
  topCarsProps?: { loading?: boolean };
}

export const MarketCarsDashboard: FC<MarketCarsDashboardProps> = ({
  cars = [],
  carsProps,
  columns = [
    "model_name",
    "make_name",
    "variant",
    "make_id",
    "produced_cars",
    "production_years",
    "current_valuation",
    "year_over_year_gain_loss",
  ],
  getPath,
  loading = false,
  makeOrDecade,
  performance,
  performanceProps = { loading: false },
  topCars = [],
  topCarsProps = { loading: false },
}) => {
  const isMobile = useMatches({ base: true, md: false });

  const [marketCarsState, setMarketCarsState] = useState<MarketCar[]>(cars);

  useEffect(() => {
    setMarketCarsState([...cars]);
  }, [cars]);

  return (
    <Stack>
      {isMobile ? (
        <Carousel
          slideGap="lg"
          slideProps={{ style: { display: "flex", alignItems: "stretch", maxWidth: "100%" } }}
          slides={[
            <Skeleton visible={topCarsProps.loading} w="100%" h="100%">
              <Card h="100%">
                <PerformingAssets
                  isMobile={isMobile}
                  getPath={getPath}
                  marketCars={topCars}
                  columns={columns.filter((c) => c !== "make_name")}
                  makeOrDecade={makeOrDecade}
                />
              </Card>
            </Skeleton>,
            <Skeleton visible={performanceProps.loading} h="100%">
              <Card h="100%">
                <MarketPerformanceChart makeOrDecade={makeOrDecade} {...performance} />
              </Card>
            </Skeleton>,
          ]}
          slideSize={{ base: "100%" }}
          styles={{ viewport: { overflow: "visible" } }}
        />
      ) : (
        <Group align="stretch" grow>
          <Card>
            <Skeleton visible={topCarsProps.loading}>
              <PerformingAssets
                columns={columns.filter((c) => c !== "make_name")}
                getPath={getPath}
                marketCars={topCars}
                makeOrDecade={makeOrDecade}
              />
            </Skeleton>
          </Card>
          <Card>
            <Skeleton visible={performanceProps.loading}>
              <MarketPerformanceChart makeOrDecade={makeOrDecade} {...performance} />
            </Skeleton>
          </Card>
        </Group>
      )}
      <Card>
        <Card.Section>
          <Skeleton visible={loading}>
            <MarketCarsTable
              columns={[...columns, "variant"]}
              decade={carsProps?.decade}
              getPath={getPath}
              hideComparison={carsProps?.hideComparison}
              makeId={carsProps?.makeId}
              marketCars={marketCarsState}
              namespace={carsProps?.namespace}
              onModelsCompare={carsProps?.onModelsCompare}
              onPageChange={carsProps?.onPageChange}
              onPageSizeChange={carsProps?.onPageSizeChange}
              onSearch={carsProps?.onSearch}
              page={carsProps?.page}
              pageSize={carsProps?.pageSize}
              pending={carsProps?.pending}
              setMarketCarsState={setMarketCarsState}
              totalCars={carsProps?.totalCars}
            />
          </Skeleton>
        </Card.Section>
      </Card>
    </Stack>
  );
};
